import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Paper, Typography } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import { createEventAttribute } from "../../../../../../data-store/actions/events-actions";
import { showSnackbar } from "../../../../../../data-store/actions/organizers-actions";
import {
  getCamposAdicionalesModalidad,
  getCamposProductos,
} from "../../../../../../services/camposAdicionalesUtils";
import { CAMPOS_CORE, PRODUCTO_CAMPO_PREFIJO } from "../../../../../../services/pivotEngine";
import PivotReportBuilder from "./PivotReportBuilder";

// Reportes guardados por evento (configuración únicamente — el resultado
// siempre se recalcula en vivo contra eventParticipants actual, nunca un
// snapshot congelado). Mismo patrón de guardado que campaignDrafts en
// CampaignEmailContainer.jsx: mapa en
// inscripcionesConfiguracion/pivotReports/{id}, sin acción de Redux nueva
// (reusa createEventAttribute, que ya existe).
const PERFILES_QUE_PUEDEN_EDITAR = ["master", "MasterOrganizador", "MasterCliente"];

export default function PivotReportContainer({
  eventoIndex,
  eventParticipants,
  registryConfiguration,
  eventSubEventsRegistry,
  registryAutorizedUsers,
  perfilUsuarioEvento,
}) {
  const dispatch = useDispatch();
  const atleteAccount = useSelector((state) => state.atlete.atleteAccount);
  const userID = useSelector((state) => state.atlete.userID);
  // Los demás perfiles (ej. "inscriptor") solo pueden ver los reportes ya
  // guardados, no crearlos ni editarlos.
  const puedeEditar = PERFILES_QUE_PUEDEN_EDITAR.includes(perfilUsuarioEvento);
  // null = nada seleccionado (vista limpia, sin formulario ni resultado
  // visibles) — "__nuevo__" = armando un reporte nuevo — cualquier otro
  // valor = id de un reporte ya guardado, mostrándose.
  const [reporteSeleccionado, setReporteSeleccionado] = useState(null);
  const [guardando, setGuardando] = useState(false);

  function nombreUsuarioActual() {
    if (!atleteAccount) return "Desconocido";
    const nombreCompleto = `${atleteAccount.nombres || ""} ${atleteAccount.apellidos || ""}`.trim();
    return nombreCompleto || atleteAccount.correo || "Desconocido";
  }

  const reportesGuardados = useMemo(() => {
    const reports = (registryConfiguration && registryConfiguration.pivotReports) || {};
    return Object.keys(reports)
      .map((id) => ({ id, ...reports[id] }))
      .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
  }, [registryConfiguration]);

  // Por defecto se muestra el primer reporte guardado (nada que el
  // organizador tenga que hacer para verlos) — sin tocar nada si ya hay
  // algo seleccionado (ej. no pisa que el usuario esté armando uno nuevo).
  useEffect(() => {
    if (reporteSeleccionado === null && reportesGuardados.length > 0) {
      setReporteSeleccionado(reportesGuardados[0].id);
    }
  }, [reportesGuardados, reporteSeleccionado]);

  const camposDisponibles = useMemo(() => {
    const dinamicos = getCamposAdicionalesModalidad(
      registryConfiguration,
      eventSubEventsRegistry
    ).filter((c) => c.iDCampo);
    const productos = getCamposProductos(eventSubEventsRegistry).map((p) => ({
      iDCampo: PRODUCTO_CAMPO_PREFIJO + p.codigoProducto,
      etiqueta: "Compró: " + (p.nombreProducto || p.codigoProducto),
      tipoCampo: "coreSelect",
    }));
    return [...CAMPOS_CORE, ...dinamicos, ...productos];
  }, [registryConfiguration, eventSubEventsRegistry]);

  const reporteActual =
    reporteSeleccionado && reporteSeleccionado !== "__nuevo__"
      ? reportesGuardados.find((r) => r.id === reporteSeleccionado)
      : null;

  function guardarComo(payload) {
    setGuardando(true);
    const nuevoId = "pivot_" + Date.now();
    const ahora = Date.now();
    dispatch(
      createEventAttribute(eventoIndex, `inscripcionesConfiguracion/pivotReports/${nuevoId}`, {
        ...payload,
        updatedAt: ahora,
        createdBy: { uid: userID, nombre: nombreUsuarioActual() },
        createdAt: ahora,
        modifiedBy: [],
      })
    ).finally(() => {
      setGuardando(false);
      setReporteSeleccionado(nuevoId);
      dispatch(showSnackbar(true, `Reporte "${payload.name}" guardado.`, 4000));
    });
  }

  function actualizar(payload) {
    setGuardando(true);
    const ahora = Date.now();
    // createEventAttribute hace un .set() del nodo completo — si no se
    // reescriben acá, createdBy/createdAt se perderían en cada
    // actualización (la ÚNICA vez que se escriben es al crear, en
    // guardarComo). modifiedBy se va acumulando, uno por cada guardado.
    dispatch(
      createEventAttribute(
        eventoIndex,
        `inscripcionesConfiguracion/pivotReports/${reporteActual.id}`,
        {
          ...payload,
          updatedAt: ahora,
          createdBy: reporteActual.createdBy || null,
          createdAt: reporteActual.createdAt || null,
          modifiedBy: [
            ...((reporteActual && reporteActual.modifiedBy) || []),
            { uid: userID, nombre: nombreUsuarioActual(), date: ahora },
          ],
        }
      )
    ).finally(() => {
      setGuardando(false);
      dispatch(showSnackbar(true, `Reporte "${payload.name}" actualizado.`, 4000));
    });
  }

  function eliminar() {
    if (!reporteActual) return;
    if (!window.confirm(`¿Borrar el reporte "${reporteActual.name}"? Esta acción no se puede deshacer.`)) {
      return;
    }
    setGuardando(true);
    dispatch(
      createEventAttribute(
        eventoIndex,
        `inscripcionesConfiguracion/pivotReports/${reporteActual.id}`,
        null
      )
    ).finally(() => {
      setGuardando(false);
      setReporteSeleccionado(null);
      dispatch(showSnackbar(true, "Reporte borrado.", 4000));
    });
  }

  return (
    <Paper style={{ padding: 20, margin: 5 }}>
      <Typography variant={"h2"} color="primary" gutterBottom>
        Reportes personalizados
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Arma tus propios cruces de datos (ej. cuántas mujeres pidieron talla M) filtrando y
        agrupando los campos de inscripción. Lo que guardes acá queda disponible cada vez que
        entres a este evento.
      </Typography>

      {reportesGuardados.length > 0 && (
        <Typography variant="subtitle1" gutterBottom>
          Consulta aquí los reportes personalizados del evento
        </Typography>
      )}
      <Box mb={2} style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {reportesGuardados.map((r) => (
          <Button
            key={r.id}
            variant={reporteSeleccionado === r.id ? "contained" : "outlined"}
            color={reporteSeleccionado === r.id ? "primary" : "default"}
            onClick={() => setReporteSeleccionado(r.id)}
          >
            {r.name}
          </Button>
        ))}
        {puedeEditar && (
          <Button
            variant={reporteSeleccionado === "__nuevo__" ? "contained" : "outlined"}
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setReporteSeleccionado("__nuevo__")}
          >
            Generar nuevo reporte
          </Button>
        )}
      </Box>

      {reporteSeleccionado && (puedeEditar || reporteSeleccionado !== "__nuevo__") && (
        <PivotReportBuilder
          key={reporteSeleccionado}
          camposDisponibles={camposDisponibles}
          eventParticipants={eventParticipants}
          eventSubEventsRegistry={eventSubEventsRegistry}
          registryAutorizedUsers={registryAutorizedUsers}
          initialConfig={reporteActual || null}
          onGuardarComo={guardarComo}
          onActualizar={actualizar}
          onEliminar={eliminar}
          guardando={guardando}
          puedeEditar={puedeEditar}
        />
      )}
    </Paper>
  );
}
